// ** React Imports
import { Link } from "react-router-dom"
import { useEffect, Fragment, useState, useMemo } from "react"
import { useTranslation } from "react-i18next"

// ** Third Party Components
import InputNumber from "rc-input-number"
import PerfectScrollbar from "react-perfect-scrollbar"
import { ShoppingCart, X, Plus, Minus, Package } from "react-feather"

// ** Reactstrap Imports
import {
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  Badge,
  Button
} from "reactstrap"

// ** Store & Actions
import { useDispatch, useSelector } from "react-redux"
import {
  fetchCart,
  removeFromCart,
  updateQuantity,
  selectCart
} from "@src/redux/slices/cartSlice"
import { selectShopCurrency } from "@src/redux/slices/shopSlice"
import { formatShopPrice } from "@src/utility/currencyUtils"
import { getLocalizedProductName } from "@src/utility/i18nUtils"

// ** Styles
import "@styles/react/libs/input-number/input-number.scss"

const DebouncedQuantityInput = ({ value, onChange, ...props }) => {
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleChange = (val) => {
    setLocalValue(val)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [localValue, value, onChange])

  return <InputNumber {...props} value={localValue} onChange={handleChange} />
}

const CartDropdown = () => {
  const { t, i18n } = useTranslation()
  
  // ** State
  const [dropdownOpen, setDropdownOpen] = useState(false)

  // ** Store Vars
  const dispatch = useDispatch()
  const cart = useSelector(selectCart)
  const currency = useSelector(selectShopCurrency)

  // ** ComponentDidMount
  useEffect(() => {
    dispatch(fetchCart())
  }, [dispatch])

  // ** Calculate totals locally for immediate updates
  const cartTotals = useMemo(() => {
    if (!cart.items || cart.items.length === 0) {
      return { subtotalWithoutDiscount: 0, subtotal: 0, discountAmount: 0 }
    }
    
    const subtotalWithoutDiscount = cart.items.reduce((sum, item) => {
      const originalPrice = Number(item.product?.subtotal || item.unitPrice || 0)
      return sum + (originalPrice * item.quantity)
    }, 0)
    
    const subtotal = cart.items.reduce((sum, item) => {
      const discountedPrice = Number(item.product?.price || 0)
      return sum + (discountedPrice * item.quantity)
    }, 0)
    
    const discountAmount = subtotalWithoutDiscount - subtotal
    
    return { subtotalWithoutDiscount, subtotal, discountAmount }
  }, [cart.items])

  // ** Function to toggle Dropdown
  const toggle = () => setDropdownOpen((prevState) => !prevState)

  // ** Renderiza os itens do carrinho
  const renderCartItems = () => {
    if (!cart.items || cart.items.length === 0) {
      return <p className="m-0 p-1 text-center">{t("Your cart is empty")}</p>
    }
    return (
      <Fragment>
        <PerfectScrollbar className="scrollable-container media-list" options={{ wheelPropagation: false }}>
          {cart.items.map((item) => {
            const hasDiscount = item.product?.discount > 0
            const discountedPrice = Number(item.product?.price || 0)
            const originalPrice = Number(item.product?.subtotal || item.unitPrice || 0)
            const itemTotal = discountedPrice * item.quantity
            
            return (
              <div key={item.id} className="list-item align-items-center">
                <div className="me-1">
                  {item.product?.image ? (
                    <img
                      src={item.product.image}
                      alt={getLocalizedProductName(item.product, i18n.language)}
                      className="rounded"
                      style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center rounded bg-light"
                      style={{ width: '50px', height: '50px' }}
                    >
                      <Package size={24} className="text-muted" />
                    </div>
                  )}
                </div>
                <div className="list-item-body flex-grow-1">
                  <X
                    size={14}
                    className="cart-item-remove"
                    onClick={() => dispatch(removeFromCart(item.id))}
                  />
                  <div className="media-heading">
                    <h6 className="cart-item-title">
                      {getLocalizedProductName(item.product, i18n.language) || t('Product not found')}
                      {hasDiscount && (
                        <Badge color="light-success" className="ms-50" pill style={{ fontSize: '10px' }}>
                          -{item.product.discount}%
                        </Badge>
                      )}
                    </h6>
                    <small className="cart-item-by">{t("Quantity")}: {item.quantity}</small>
                  </div>
                  <div className="cart-item-qty">
                    <DebouncedQuantityInput
                      min={1}
                      max={item.product?.qtdMax > 0 ? item.product.qtdMax : 99}
                      upHandler={<Plus size={14} />}
                      downHandler={<Minus size={14} />}
                      className="cart-input"
                      value={item.quantity}
                      onChange={q => dispatch(updateQuantity({ cartItemId: item.id, quantity: q }))}
                    />
                  </div>
                  <div className="cart-item-price">
                    <h5 className="mb-0">{formatShopPrice(itemTotal, currency)}</h5>
                    {hasDiscount && (
                      <small className="text-muted text-decoration-line-through">
                        {formatShopPrice(originalPrice * item.quantity, currency)}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </PerfectScrollbar>
        <li key="cart-footer" className="dropdown-menu-footer">
          {cartTotals.discountAmount > 0 && (
            <>
              <div className="d-flex justify-content-between">
                <small className="text-muted">{t("Subtotal")}:</small>
                <small className="text-muted text-decoration-line-through">
                  {formatShopPrice(cartTotals.subtotalWithoutDiscount, currency)}
                </small>
              </div>
              <div className="d-flex justify-content-between">
                <small className="text-success">{t("Discount")}:</small>
                <small className="text-success">
                  -{formatShopPrice(cartTotals.discountAmount, currency)}
                </small>
              </div>
            </>
          )}
          <div className="d-flex justify-content-between mb-1">
            <h6 className="fw-bolder mb-0">{t("Total")}:</h6>
            <h6 className="text-primary fw-bolder mb-0">
              {formatShopPrice(cartTotals.subtotal, currency)}
            </h6>
          </div>
          <Button
            tag={Link}
            to="/shop/cart"
            color="secondary"
            block
            onClick={toggle}
            className="mb-50"
          >
            {t("View Cart")}
          </Button>
          <Button
            tag={Link}
            to="/shop/checkout"
            color="primary"
            block
            onClick={toggle}
          >
            {t("Proceed to Checkout")}
          </Button>
        </li>
      </Fragment>
    )
  }

  return (
    <Dropdown
      isOpen={dropdownOpen}
      toggle={toggle}
      tag="li"
      className="dropdown-cart nav-item me-25"
    >
      <DropdownToggle tag="a" className="nav-link position-relative">
        <ShoppingCart className="ficon" />
        {cart.items && cart.items.length > 0 ? (
          <Badge pill color='primary' className='badge-up'>
            {cart.items.length}
          </Badge>
        ) : null}
      </DropdownToggle>
      <DropdownMenu
        end
        tag="ul"
        className="dropdown-menu-media dropdown-cart mt-0"
      >
        <li className="dropdown-menu-header">
          <DropdownItem tag="div" className="d-flex" header>
            <h4 className="notification-title mb-0 me-auto">{t("Cart")}</h4>
            <Badge color="light-primary" pill>
              {cart.items?.length || 0} {t("Items")}
            </Badge>
          </DropdownItem>
        </li>
        {renderCartItems()}
      </DropdownMenu>
    </Dropdown>
  )
}

export default CartDropdown
